import React, { useEffect } from 'react';
import {
  Button, Form, Typography,
} from 'antd';

import DrawerFrame from 'components/components/Drawer/Frame';
import Input from 'components/components/Input';
import { contactEmailRules, startupNameRules } from 'components/VentureBuilderForms';
import { scrollToFirstError } from 'components/components/FormBuilder/utils/helpers';
import { useCreateManagedStartup, useUpdateManagedStartup } from 'packages/venture_builder_repository';
import { MANAGED_STARTUP_INITIAL_ALLOCATION } from 'packages/venture_builder_repository/utils/constants';
import { ManagedStartupDrawerProps, ManagedStartupFormValues } from 'types/ventureBuilders';
import { parseNumberToPrice } from 'utils/helpers/priceFormatters';
import showNotification from 'services/utils/showNotification';

const ManagedStartupDrawer = ({
  open, ventureBuilderId, startup, onClose,
}: ManagedStartupDrawerProps) => {
  const [form] = Form.useForm<ManagedStartupFormValues>();
  const create = useCreateManagedStartup(ventureBuilderId);
  const update = useUpdateManagedStartup();
  const isPending = create.isPending || update.isPending;

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ name: startup?.name || '', email: startup?.email || '' });
    }
  }, [open, startup, form]);

  const handleSubmit = (values: ManagedStartupFormValues) => {
    const callbacks = {
      onSuccess: () => {
        showNotification(startup ? 'Startup has been updated' : 'Startup has been added');
        onClose();
      },
      onError: (error: Error) => showNotification(error.message, true, 400),
    };
    if (startup) {
      update.mutate({ id: startup.id, name: values.name }, callbacks);
    } else {
      create.mutate({ name: values.name, email: values.email }, callbacks);
    }
  };

  return (
    <DrawerFrame
      forceRender
      title={startup ? 'Edit startup' : 'Add startup'}
      open={open}
      onClose={onClose}
    >
      <Form form={form} className="form-standalone" onFinish={handleSubmit} onFinishFailed={scrollToFirstError}>
        <Input name="name" label="Startup name" rules={startupNameRules}>
          <Input.InputField placeholder="Enter startup name" />
        </Input>
        {!startup && (
          <>
            <Input name="email" label="Contact email" rules={contactEmailRules}>
              <Input.InputField placeholder="Enter startup contact email" />
            </Input>
            <Typography.Paragraph type="secondary">
              This startup gets its own wallet with
              {' '}
              {parseNumberToPrice(MANAGED_STARTUP_INITIAL_ALLOCATION, 'AED')}
              .
              Existing startup wallets stay unchanged.
            </Typography.Paragraph>
          </>
        )}
        <div className="drawer-buttons">
          <Button className="secondary-button" onClick={onClose}>Cancel</Button>
          <Button className="primary-button" htmlType="submit" loading={isPending}>
            {startup ? 'Save changes' : 'Add startup'}
          </Button>
        </div>
      </Form>
    </DrawerFrame>
  );
};

export default ManagedStartupDrawer;
